import { useCallback, useEffect, useRef, useState } from 'react';
import type { Scope, SpraypaintBase } from 'spraypaint';
import { reportError } from '../utilities/reportError';

export const useSpraypaintNamedParams = <TBase extends SpraypaintBase, TQuery = unknown, TMeta = unknown>({
  initialScope,
  initialQuery = null,
  initialPageSize = null,
  initialMeta = null,
  initialExtraParams = null,
  initialOrder = null,
}: {
  initialScope: Scope<TBase>;
  initialQuery?: TQuery;
  initialPageSize?: number;
  initialMeta?: TMeta;
  initialExtraParams?: Record<string, unknown>;
  initialOrder?: Record<string, unknown>;
}) =>
  useSpraypaint<TBase, TQuery, TMeta>(
    initialScope,
    initialQuery,
    initialPageSize,
    initialMeta,
    initialExtraParams,
    initialOrder,
  );

/**
 * @deprecated (TimKrins) use useSpraypaintNamedParams instead please
 */
export function useSpraypaint<TBase extends SpraypaintBase, TQuery = any, TMeta = any>(
  initialScope: Scope<TBase>,
  initialQuery: TQuery = null,
  initialPageSize: number = null,
  initialMeta: TMeta = null,
  initialExtraParams: Record<string, unknown> = null,
  initialOrder: Record<string, unknown> = null,
) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [scope, setScope] = useState(initialScope);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [page, setPage] = useState<number>(1);
  const [query, setQuery] = useState<TQuery>(initialQuery);
  const [data, setData] = useState<TBase[]>([]);
  const [meta, setMeta] = useState<TMeta>(initialMeta);
  const [order, setOrder] = useState<any>(initialOrder);
  const [merge, setMerge] = useState(null);
  const [extraParams, setExtraParams] = useState(initialExtraParams);
  const lastExecutedScopeRef = useRef<object>();

  useEffect(() => {
    fetchData();
  }, [scope, order, pageSize, page, query, merge, extraParams]);

  const fetchData = useCallback(() => {
    if (!scope) return Promise.resolve();
    setIsLoading(true);
    let scopeForExecution = scope.where(query);

    if (order) scopeForExecution = scopeForExecution.order(order);
    if (pageSize) scopeForExecution = scopeForExecution.per(pageSize);
    if (page) scopeForExecution = scopeForExecution.page(page);
    if (merge) scopeForExecution = scopeForExecution.merge(merge);
    if (extraParams) scopeForExecution = scopeForExecution.extraParams(extraParams);

    // make sure we only use the last executed promise result
    lastExecutedScopeRef.current = scopeForExecution;

    return scopeForExecution
      .all()
      .then(({ data: spraypaintData, meta: spraypaintMeta }) => {
        if (lastExecutedScopeRef.current === scopeForExecution) {
          setError(null);
          setData(spraypaintData);
          setMeta(spraypaintMeta as TMeta);
        }
      })
      .catch((e) => {
        if (lastExecutedScopeRef.current === scopeForExecution) {
          reportError(e);
          setError(e);
        }
      })
      .then(() => {
        if (lastExecutedScopeRef.current === scopeForExecution) {
          setIsLoading(false);
          setIsInitialLoading(false);
        }
      });
  }, [scope, order, pageSize, page, query, merge, extraParams]);

  return {
    data,
    error,
    extraParams,
    fetchData,
    isInitialLoading,
    isLoading,
    meta,
    order,
    page,
    pageSize,
    query,
    scope,
    setExtraParams,
    setMerge,
    setOrder,
    setPage,
    setPageSize,
    setQuery,
    setScope,
  };
}
